import type { PreviewCard } from '../../../services/api'
import type { CarouselPin } from './CarouselEditor'

// ACE resolves the rail, and the CMS never re-orders it. The preview request
// carries the pins, so previewWidgetItems returns the woven sequence, and the
// strip renders that response in the order it arrived — the same way the artwork
// template editor shows a server-rendered PNG rather than an approximation of
// one. Every edit refetches, and the strip fades while it does.
//
// The CMS adds exactly one thing on top: which tiles are its own pins, so it can
// mark them and let the merchandiser drag them. That is a join on ids the CMS
// itself authored, not a copy of ACE's placement, so it can never put a card in
// the wrong place. If it ever went stale it would fail visibly — a tile losing
// its pin glyph — rather than showing a plausible, wrong rail.

/** The `cardId` ACE gives the card built from this pin. Content pins share the
 * id of the content itself (layout_dynamic.go, layout_vod.go); a custom pin's
 * card carries the pin id (layout_pins.go: customPinCard). */
export const pinCardId = (pin: CarouselPin): string | undefined => {
  if (pin.type === 'match') return pin.match_id
  if (pin.type === 'vod') return pin.asset_id
  return pin.id
}

/** The pins in the order ACE places them: by slot, ties keeping array order. */
const bySlot = (pins: CarouselPin[]): CarouselPin[] =>
  [...pins].sort((a, b) => a.position - b.position)

/**
 * The authored pin behind each returned card, index-aligned with `cards` and
 * undefined for a query result.
 *
 * Pins are paired to tiles left to right and each is used once, so two pins on
 * the same content — which resolve to one card id — still map to one tile each.
 * ACE emits pins in slot order, so left-to-right pairing matches slot order.
 */
export const pinsByTile = (
  pins: CarouselPin[],
  cards: PreviewCard[],
): Array<CarouselPin | undefined> => {
  const unclaimed = new Map<string, CarouselPin[]>()
  for (const pin of bySlot(pins)) {
    const id = pinCardId(pin)
    if (!id) continue
    const queue = unclaimed.get(id)
    if (queue) queue.push(pin)
    else unclaimed.set(id, [pin])
  }
  return cards.map(card => unclaimed.get(card.cardId)?.shift())
}

/**
 * The `position` to write when the pin displayed at tile `from` is dropped at
 * `boundary` — the gap before that tile index, clamped to the tiles the strip
 * actually holds. `from` is -1 for a pin the window does not show.
 *
 * The caret is a gap in the strip AS DISPLAYED, which still holds the dragged
 * tile. Lifting that tile out closes one gap to its left, so every boundary past
 * `from` shifts down by one. Without that adjustment a rightward drag lands one
 * slot too far right, and releasing a card back onto its own right half reads as
 * a move rather than the no-op it looks like.
 *
 * A caret expresses ORDER, and this writes the config that orders the pin that
 * way. It does not promise a rendered index, and the CMS is in no position to:
 * `position` is a lower bound on where a card appears, not the place it appears.
 * ACE leaves no holes, so when the query runs short the pins pack up the tail
 * (layout_pins.go: "the query ran out and the pins pack the tail"). A fully
 * curated rail is the extreme — every position collapses to a rank, and a pin at
 * position 4 renders first if it sorts first. How far a pin rides up depends on
 * how much content the query supplies, which is ACE's business and nothing the
 * editor can compute.
 *
 * So the strip promises nothing about the landing slot. It writes the order the
 * merchandiser drew, refetches, and shows where the server actually put it.
 */
export const dropPosition = (boundary: number, from: number, tileCount: number): number => {
  const rank = boundary - (from >= 0 && boundary > from ? 1 : 0)
  return Math.max(0, Math.min(rank, tileCount - 1))
}

/**
 * The pins array to save when `pinId` is dropped at the caret `boundary`. Null
 * when the drop changes nothing, so a drag that goes nowhere costs no refetch.
 *
 * This writes config; it does not decide where the card renders. ACE does that,
 * and the strip shows the answer once the refetch lands.
 */
export const dropPin = (
  pins: CarouselPin[],
  tilePins: Array<CarouselPin | undefined>,
  pinId: string,
  boundary: number,
  tileCount: number,
): CarouselPin[] | null => {
  const moving = pins.find(p => p.id === pinId)
  if (!moving) return null

  const from = tilePins.findIndex(p => p?.id === pinId)
  const position = dropPosition(boundary, from, tileCount)
  if (from === position) return null

  const rest = pins.filter(p => p.id !== pinId)
  const moved = { ...moving, position }

  // Array order is ACE's tie-break between two pins on one position (the rule
  // card-carousel.html#pins documents), so it is the only thing that decides
  // which of a tie renders first — and for everything else it does not matter.
  const tied = rest.filter(p => p.position === position)
  if (tied.length === 0) return [...rest, moved]

  // Of that tie, the pins the caret was drawn past keep their lead and the rest
  // give way. Reading this off the strip rather than off the drag's direction is
  // what lets a drop land BETWEEN two pins that already share a position; a
  // direction rule can only put the card ahead of both or behind both.
  const shownRank = (pin: CarouselPin) => {
    const at = tilePins.findIndex(p => p?.id === pin.id)
    return at - (from >= 0 && at > from ? 1 : 0)
  }
  const ahead = tied.filter(p => shownRank(p) < position)
  const at = ahead.length
    ? rest.indexOf(ahead[ahead.length - 1]) + 1
    : rest.indexOf(tied[0])
  return [...rest.slice(0, at), moved, ...rest.slice(at)]
}

/**
 * Pins that authored a slot inside the previewed window but came back with no
 * card — unfinished, or content ACE could not resolve. ACE drops them silently
 * and the rail closes up, so the editor has to say so.
 *
 * A pin past the end of the window is not missing, it is simply off this page;
 * that is only knowable when the response reports more content after it.
 */
export const pinsNotRendered = (
  pins: CarouselPin[],
  tilePins: Array<CarouselPin | undefined>,
  windowLength: number,
  hasNext: boolean,
): CarouselPin[] => {
  const shown = new Set(tilePins.filter(Boolean).map(p => p?.id))
  return pins.filter(p => !shown.has(p.id) && (!hasNext || p.position < windowLength))
}
